import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { CoversheetTemplate } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/messages.pb';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Button,
  Modal,
  ModalControlModalProps,
  useControlledField,
  Table,
  CheckboxField,
  SwitchField,
  useFormField,
  useDebouncedValue,
  SearchField,
  TextLink,
  useAlert,
  useTooltip,
} from '@frontend/design-system';

type AssignLocationAccessModalProps = {
  mode: 'action' | 'progression';
  modalProps: ModalControlModalProps;
  locationsWithAccess: string[];
  isTemplateDataLoading?: boolean;
  onSave: (locations: string[]) => Promise<CoversheetTemplate> | undefined | void;
};

type LocationData = {
  id: string;
  locationName: string;
  access: boolean;
  selected: boolean;
};

export const AssignLocationAccessModal = ({
  mode,
  locationsWithAccess,
  modalProps,
  isTemplateDataLoading,
  onSave,
}: AssignLocationAccessModalProps) => {
  const { t } = useTranslation('fax');
  const { selectedLocationIds, locationData } = useAppScopeStore();
  const [filter, setFilter] = useState<'all' | 'access' | 'no-access'>('all');
  const allLocationsCount = selectedLocationIds.length;
  const locationsWithAccessCount = locationsWithAccess.filter((id) => selectedLocationIds.includes(id)).length;
  const [isSaving, setIsSaving] = useState(false);
  const alert = useAlert();

  const { Tooltip, triggerProps, tooltipProps } = useTooltip();

  const [locationsData, setLocationsData] = useState(() =>
    selectedLocationIds.map((locationId) => ({
      id: locationId,
      locationName: locationData[locationId]?.name ?? `Unknown Location ${locationId}`,
      access: mode === 'progression' ? false : locationsWithAccess.includes(locationId),
      selected: mode === 'progression' ? locationsWithAccess.includes(locationId) : false,
    }))
  );

  useEffect(() => {
    const newLocationsData = selectedLocationIds.map((locationId) => ({
      id: locationId,
      locationName: locationData[locationId]?.name ?? `Unknown Location ${locationId}`,
      access: mode === 'progression' ? false : locationsWithAccess.includes(locationId),
      selected: mode === 'progression' ? locationsWithAccess.includes(locationId) : false,
    }));
    setLocationsData(newLocationsData);
  }, [locationsWithAccess]);

  const customModalProps = {
    ...modalProps,
    onClose: () => {
      setFilter('all');
      setLocationsData((locationData) =>
        locationData.map((location) => ({
          ...location,
          selected: mode === 'progression' ? locationsWithAccess.includes(location.id) : false,
        }))
      );
      modalProps.onClose();
    },
  };

  const searchFieldProps = useFormField({
    type: 'text',
    value: '',
  });

  const debouncedSearch = useDebouncedValue(searchFieldProps.value);

  const filteredLocationsData = locationsData.filter((location) => {
    if (filter !== 'all' && location.access !== (filter === 'access')) {
      return false;
    }
    if (debouncedSearch && !location.locationName.toLowerCase().includes(debouncedSearch.toLowerCase())) {
      return false;
    }
    return true;
  });

  const isAllSelected = !!filteredLocationsData.length && filteredLocationsData.every((location) => location.selected);

  const totalCount = mode === 'progression' ? locationsData.length : filteredLocationsData.length;
  const selectedCount = filteredLocationsData.reduce((acc, location) => {
    if (location.selected) return acc + 1;
    return acc;
  }, 0);

  const isLoading = isSaving || !!isTemplateDataLoading;

  const enableGrantAccess =
    !isLoading &&
    filteredLocationsData.some((location) => location.selected && !locationsWithAccess.includes(location.id));
  const enableRevokeAccess =
    !isLoading &&
    filteredLocationsData.some((location) => location.selected && locationsWithAccess.includes(location.id));

  const onSelectAllToggle = () => {
    setLocationsData((prevLocations) =>
      prevLocations.map((location) => {
        if (mode === 'progression' && locationsWithAccess.includes(location.id)) return location;
        return {
          ...location,
          selected: !isAllSelected,
        };
      })
    );
  };

  const onAccessToggle = (id: string) => {
    setLocationsData((prevLocations) =>
      prevLocations.map((location) => {
        if (location.id === id) {
          return { ...location, selected: !location.selected };
        }
        return location;
      })
    );
  };

  const grantAccess = async () => {
    if (mode === 'progression') return;

    const locations = [
      ...new Set([
        ...locationsWithAccess,
        ...filteredLocationsData.filter((location) => location.selected).map((location) => location.id),
      ]),
    ];
    if (!locations.length) {
      alert.error(t('Coversheet must be assigned to at least one location'));
      return;
    }
    try {
      setIsSaving(true);
      await onSave(locations);
      customModalProps.onClose();
    } catch (e) {
      alert.error(t('Error granting access'));
    } finally {
      setIsSaving(false);
    }
  };

  const revokeAccess = async () => {
    if (mode === 'progression') return;

    const locations = locationsWithAccess.filter(
      (id) => !filteredLocationsData.find((location) => location.selected && id === location.id)
    );
    if (!locations.length) {
      alert.error(t('Coversheet must be assigned to at least one location'));
      return;
    }
    try {
      setIsSaving(true);
      await onSave(locations);
      customModalProps.onClose();
    } catch (e) {
      alert.error(t('Error revoking access'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateLocationAccess = () => {
    const locations = filteredLocationsData.filter((location) => location.selected).map((location) => location.id);
    if (!locations.length) {
      alert.error(t('Coversheet must be assigned to at least one location'));
      return;
    }
    onSave(locations);
  };

  return (
    <Modal
      {...customModalProps}
      css={{
        maxWidth: '800px',
        maxHeight: '800px',
        padding: theme.spacing(3, 1),
      }}
    >
      <Modal.Header onClose={customModalProps.onClose}>{t('Location Access')}</Modal.Header>
      <Modal.Label>{t('Which locations will have access to this template?')}</Modal.Label>
      <Modal.Body css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
        {mode === 'action' && (
          <div
            css={{
              display: 'flex',
              gap: theme.spacing(1),
              flexWrap: 'wrap',
              [`@media only screen and (max-width: ${breakpoints.small.min}px)`]: {
                flexDirection: 'column',
                alignItems: 'flex-start',
              },
            }}
          >
            <Button
              disabled={isLoading}
              variant={filter === 'all' ? 'primary' : 'secondary'}
              onClick={() => setFilter('all')}
            >
              {t('All locations {{count}}', { count: allLocationsCount })}
            </Button>
            <Button
              disabled={isLoading}
              variant={filter === 'access' ? 'primary' : 'secondary'}
              onClick={() => setFilter('access')}
            >
              {t('With Access {{count}}', { count: locationsWithAccessCount })}
            </Button>
            <Button
              disabled={isLoading}
              variant={filter === 'no-access' ? 'primary' : 'secondary'}
              onClick={() => setFilter('no-access')}
            >
              {t('Without Access  {{count}}', { count: allLocationsCount - locationsWithAccessCount })}
            </Button>
          </div>
        )}

        <div
          css={{
            display: 'flex',
            columnGap: theme.spacing(2.5),
            rowGap: theme.spacing(1),
            paddingLeft: theme.spacing(1.5),
            alignItems: 'center',
            '.location-access-checkbox-field': { padding: theme.spacing(1.2, 0) },
            [`@media only screen and (max-width: ${breakpoints.small.min}px)`]: {
              flexDirection: 'column',
              alignItems: 'flex-end',
            },
          }}
        >
          {!!filteredLocationsData.length && (
            <>
              <CustomCheckboxField
                name='Select All'
                value={selectedCount > 0 || isAllSelected}
                indeterminate={selectedCount > 0 && selectedCount < totalCount}
                onChange={onSelectAllToggle}
                label={t('Selected {{count}} of {{total}}', {
                  count: selectedCount,
                  total: totalCount,
                })}
                disabled={isLoading}
              />
            </>
          )}
          {!selectedCount || mode === 'progression' ? (
            <SearchField
              className='test'
              containerCss={css`
                margin-left: auto;
              `}
              {...searchFieldProps}
              name='location-search'
              disabled={isLoading}
            />
          ) : mode === 'action' ? (
            <div
              css={{
                display: 'flex',
                gap: theme.spacing(1),
                padding: theme.spacing(1.2, 0),
                marginLeft: 'auto',
              }}
            >
              {['all', 'no-access'].includes(filter) && (
                <TextLink css={textLinkStyle(!enableGrantAccess)} disabled={!enableGrantAccess} onClick={grantAccess}>
                  {t('Grant Access')}
                </TextLink>
              )}
              {['all', 'access'].includes(filter) && (
                <TextLink
                  css={textLinkStyle(!enableRevokeAccess)}
                  disabled={!enableRevokeAccess}
                  onClick={revokeAccess}
                >
                  {t('Revoke Access')}
                </TextLink>
              )}
            </div>
          ) : null}
        </div>
        <Table
          data={filteredLocationsData ?? []}
          wrapperStyle={css`
            max-height: 268px;
            width: ${mode === 'action' ? '530px' : '430px'};
            .empty__state__container {
              height: 210px;
            }
            @media (max-width: ${breakpoints.medium.min}px) {
              width: ${mode === 'action' ? '468px' : '430px'};
            }
            @media (max-width: ${breakpoints.small.min}px) {
              width: 380px;
            }
          `}
          emptyStateConfig={{
            header: t('No locations data'),
          }}
          colConfig={[
            {
              id: 'checkbox',
              accessor: (location) => location.selected,
              Header: '',
              width: mode === 'action' ? 45 : 25,
              minWidth: mode === 'action' ? 45 : 25,
              disableSortBy: true,
              cellRenderer: (_name, location) => {
                if (!location) return null;
                return (
                  <>
                    <CustomCheckboxField
                      name={location.locationName ?? location.id}
                      onChange={() => onAccessToggle(location.id)}
                      value={location.selected}
                      disabled={mode === 'action' ? isLoading : locationsWithAccess.includes(location.id)}
                      {...(mode === 'progression' ? triggerProps : {})}
                    />
                    {mode === 'progression' && locationsWithAccess.includes(location.id) && (
                      <Tooltip {...tooltipProps}>{t('Cannot revoke fax location access')}</Tooltip>
                    )}
                  </>
                );
              },
            },
            {
              id: 'location',
              Header: t('Location'),
              accessor: (location) => location.locationName,
              width: 150,
              cellRenderer: (_name, location) => location?.locationName ?? t('Unknown Location'),
              sortType: 'string',
            },
            ...(mode === 'action'
              ? [
                  {
                    id: 'access',
                    Header: t('Access Off/On'),
                    accessor: (location: LocationData) => location?.access,
                    cellRenderer: (_name: any, location: LocationData | undefined) => {
                      if (!location) return null;
                      return (
                        <CustomSwitchField
                          name={location.locationName ?? location.id}
                          value={location.access}
                          disabled={isLoading}
                        />
                      );
                    },
                  },
                ]
              : []),
          ]}
        />
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: mode === 'action' ? t('Done') : t('Continue'),
          onClick: mode === 'action' ? customModalProps.onClose : handleUpdateLocationAccess,
          disabled: isLoading,
          loading: isLoading,
        }}
        secondary={
          mode === 'progression'
            ? {
                label: t('Cancel'),
                onClick: customModalProps.onClose,
              }
            : undefined
        }
      />
    </Modal>
  );
};

type CustomCheckboxFieldProps = {
  name: string;
  label?: string;
  onChange: (value: boolean) => void;
  value?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
};

const CustomCheckboxField = ({
  name,
  onChange,
  value,
  label,
  indeterminate,
  disabled,
  ...rest
}: CustomCheckboxFieldProps) => {
  const fieldProps = useControlledField({
    onChange: (value) => {
      onChange(value);
    },
    type: 'checkbox',
    value,
  });

  return (
    <CheckboxField
      className='location-access-checkbox-field'
      {...fieldProps}
      label={label ?? ''}
      name={name}
      indeterminate={indeterminate}
      disabled={disabled}
      {...rest}
    />
  );
};

type CustomSwitchFieldProps = {
  name: string;
  value?: boolean;
  disabled?: boolean;
};

const CustomSwitchField = ({ name, value, disabled }: CustomSwitchFieldProps) => {
  const fieldProps = useControlledField({
    onChange: () => {},
    type: 'switch',
    value,
  });

  return <SwitchField {...fieldProps} label='' name={name} disabled={disabled} />;
};

const textLinkStyle = (isLoading: boolean) => css`
  cursor: pointer;
  pointer-events: ${isLoading ? 'none' : 'auto'};
  font-weight: ${theme.font.weight.bold};
  color: ${isLoading ? theme.colors.neutral30 : theme.colors.primary50};
  font-size: ${theme.font.size.medium};
  :hover,
  :focus {
    text-decoration: none;
  }
`;
